import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { Separator as SeparatorPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "helpers/cn";
import { useIsMobile } from "hooks";

// Spacing defaults to `none`: the parent owns spacing (see core/Flex `gap`).
// The named steps exist so call sites migrated off antd can restate the margin
// it used to inject, rather than inheriting it invisibly.
const dividerVariants = cva("border-border", {
  variants: {
    orientation: {
      horizontal: "w-full border-t",
      vertical: "relative -top-[0.06em] inline-block h-[0.9em] border-l align-middle"
    },
    spacing: { none: "", xs: "", sm: "", md: "" }
  },
  compoundVariants: [
    { orientation: "horizontal", spacing: "xs", class: "my-2" },
    { orientation: "horizontal", spacing: "sm", class: "my-4" },
    { orientation: "horizontal", spacing: "md", class: "my-6" },
    { orientation: "vertical", spacing: "xs", class: "mx-2" },
    { orientation: "vertical", spacing: "sm", class: "mx-4" },
    { orientation: "vertical", spacing: "md", class: "mx-6" }
  ],
  defaultVariants: { orientation: "horizontal", spacing: "none" }
});

// Matches antd's with-text gutter (1em either side of the label).
const dividerLabelVariants = cva("flex w-full items-center gap-[1em]", {
  variants: { spacing: { none: "", xs: "my-2", sm: "my-4", md: "my-6" } },
  defaultVariants: { spacing: "none" }
});

type DividerProps = Omit<
  ComponentProps<typeof SeparatorPrimitive.Root>,
  "orientation" | "children" | "decorative" | "asChild"
> &
  VariantProps<typeof dividerVariants> & {
    children?: ReactNode;
    hideOnMobile?: boolean;
  };

const Divider = ({
  className,
  orientation,
  spacing,
  children,
  hideOnMobile,
  ...props
}: DividerProps) => {
  const isMobile = useIsMobile();

  if (isMobile && hideOnMobile) {
    return null;
  }

  if (children == null) {
    return (
      <SeparatorPrimitive.Root
        decorative
        className={cn(dividerVariants({ orientation, spacing }), className)}
        data-slot="divider"
        orientation={orientation ?? "horizontal"}
        {...props}
      />
    );
  }

  const rail = (
    <SeparatorPrimitive.Root
      decorative
      className={cn(dividerVariants({ orientation: "horizontal" }), "flex-1")}
    />
  );

  return (
    <div
      className={cn(dividerLabelVariants({ spacing }), className)}
      data-slot="divider"
      {...props}
    >
      {rail}
      {children}
      {rail}
    </div>
  );
};

export { Divider, dividerVariants };
